/**
 * L'ATELIER D'ÉCRITURE — concevoir un exercice, ou relire un existant.
 *
 * Le fondateur, 14 septembre 2026 : « Les exercices doivent être éditables afin
 * qu'on puisse les supprimer, les modifier, les mettre à jour, et en créer de
 * nouveaux, from scratch ou bien via appel LLM depuis la console. »
 *
 * ─── Ce que cet écran fait, et dans quel ordre ────────────────────────────
 *
 *  1. LE CADRAGE d'abord — l'année, la séquence quand elle s'applique, le
 *     savoir, la notion. Rien ne se conçoit dans le vide : la matière et
 *     l'année de l'exercice viennent du SAVOIR, jamais d'une saisie. Le
 *     professeur ne peut donc pas se tromper dessus.
 *  2. L'ÉCRITURE ensuite, sur toute la largeur. Le fondateur : « Au lieu
 *     d'avoir plusieurs cases, il est mieux d'avoir un bouton "aperçu" [...]
 *     permettant d'occuper l'ampleur de la page pour le confort de
 *     l'enseignant pendant la conception. »
 *  3. L'APERÇU bascule l'écran entier dans la vue de l'élève, à la taille
 *     réelle, puis le rend à l'édition. On vérifie en voyant, pas en imaginant.
 *
 * ─── Ce qu'il ne fait pas ─────────────────────────────────────────────────
 *
 * Il ne publie pas. Un item écrit ici naît en BROUILLON : entre l'atelier et
 * l'élève, il y a une relecture. C'est ce qui distingue cette voie de la
 * génération à la demande, qui publie sans revue parce qu'elle sert un élève
 * précis dans l'instant.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { MATIERES, NIVEAUX_SCOLAIRES, SEQUENCES_MATHS, type SequenceMaths } from '@silex/shared';

import { api, ApiError } from '../api/client';
import {
  notionsOrdonnees,
  savoirsDuCadrage,
  sequenceApplicable,
  type SavoirCadrage,
} from '../lib/cadrage-atelier';
import { matiereLabel } from '../lib/suivi-format';

import {
  ChampsFigureFonction,
  ETAT_FIGURE_PAR_DEFAUT,
  figureIntracable,
  specDeLEtat,
  type EtatFigure,
} from '../components/ChampsFigureFonction';

/** Les formes qu'un professeur écrit à la main, sans outil supplémentaire. */
const FORMES = [
  { id: 'qcm', label: 'Choix multiple' },
  { id: 'reponse_courte', label: 'Réponse courte' },
] as const;

const EXIGENCES = [
  { id: 'accessible', label: 'Accessible' },
  { id: 'intermediaire', label: 'Intermédiaire' },
  { id: 'exigeant', label: 'Exigeant' },
] as const;

interface OptionEcrite {
  readonly id: string;
  texte: string;
  /** Vide = c'est la bonne réponse. Sinon, le code de l'erreur visée. */
  erreurType: string;
}

const OPTIONS_VIDES: OptionEcrite[] = [
  { id: 'a', texte: '', erreurType: '' },
  { id: 'b', texte: '', erreurType: '' },
];

export function AtelierExercicePage() {
  const navigate = useNavigate();

  /* ── Le cadrage ──────────────────────────────────────────────────────── */
  const [matiere, setMatiere] = useState<string>('mathematiques');
  const [niveau, setNiveau] = useState<string>('sec2');
  const [sequence, setSequence] = useState<SequenceMaths | null>(null);
  const [savoirId, setSavoirId] = useState<string>('');
  const [notionCode, setNotionCode] = useState<string>('');
  const [savoirs, setSavoirs] = useState<readonly SavoirCadrage[] | null>(null);

  /* ── L'exercice ──────────────────────────────────────────────────────── */
  const [forme, setForme] = useState<string>('qcm');
  const [exigence, setExigence] = useState<string>('intermediaire');
  const [difficulte, setDifficulte] = useState<number>(3);
  const [titre, setTitre] = useState('');
  const [texte, setTexte] = useState('');
  const [consigne, setConsigne] = useState('');
  const [explication, setExplication] = useState('');
  const [options, setOptions] = useState<OptionEcrite[]>(OPTIONS_VIDES);
  const [reponses, setReponses] = useState('');

  const [apercu, setApercu] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  /* ── LA FIGURE ─────────────────────────────────────────────────────────
     Une seule courbe, alors que le contrat en autorise deux (« celle qu'on
     cherche » et « la tienne ») : le besoin est de TRACER une fonction. La
     seconde se rajoutera si elle est demandée. */
  const [avecFigure, setAvecFigure] = useState(false);
  const [figure, setFigure] = useState<EtatFigure>(ETAT_FIGURE_PAR_DEFAUT);

  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  /* La figure courante et sa résolution. `resoudreFigure` rend `null` quand le
     moteur ne sait pas la tracer : c'est à la fois l'aperçu et le contrôle. */
  const figureSpec = specDeLEtat(avecFigure, figure);
  const figureInsoluble = figureIntracable(avecFigure, figure);

  useEffect(() => {
    let vivant = true;
    void api
      .savoirsDuCorpus()
      .then((r) => vivant && setSavoirs(r.savoirs as readonly SavoirCadrage[]))
      .catch((e: unknown) =>
        vivant ? setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue') : undefined,
      );
    return () => {
      vivant = false;
    };
  }, []);

  const avecSequence = sequenceApplicable(matiere, niveau);
  const savoirsVisibles = useMemo(
    () => savoirsDuCadrage(savoirs ?? [], { matiere, niveau, sequence }),
    [savoirs, matiere, niveau, sequence],
  );
  const savoirChoisi = savoirsVisibles.find((s) => s.id === savoirId);
  const notions = notionsOrdonnees(savoirChoisi);

  /* Un cadrage qui change invalide ce qu'il commande : on ne garde pas un
     savoir d'une autre année, ni une notion d'un autre savoir. */
  useEffect(() => {
    if (savoirId && !savoirsVisibles.some((s) => s.id === savoirId)) setSavoirId('');
  }, [savoirsVisibles, savoirId]);
  useEffect(() => {
    if (notionCode && !notions.some((n) => n.code === notionCode)) setNotionCode('');
  }, [notions, notionCode]);

  const bonneOption = options.find((o) => o.erreurType.trim() === '');
  const pretAEcrire = notionCode !== '';

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErreur(null);
    setSucces(null);
    setEnvoi(true);
    try {
      /* On REFUSE d'enregistrer une figure que le moteur ne sait pas tracer.
         Le serveur pose la même garde — ici, c'est pour que l'auteur l'apprenne
         devant son écran plutôt qu'après un aller-retour. */
      if (figureInsoluble) {
        setErreur(
          'Cette figure ne se trace pas avec ces valeurs. Corrigez le domaine ou les coefficients — elle ne serait jamais servie à l’élève.',
        );
        setEnvoi(false);
        return;
      }

      const item = await api.creerItemCorpus({
        notionCode,
        type: forme,
        exigence,
        difficulteAuteur: difficulte,
        methodeVerification: 'deterministe',
        titre,
        texte,
        consigne,
        explication,
        ...(forme === 'qcm'
          ? {
              options: options
                .filter((o) => o.texte.trim() !== '')
                .map((o) => ({
                  id: o.id,
                  texte: o.texte,
                  ...(o.erreurType.trim() === '' ? {} : { erreurType: o.erreurType.trim() }),
                })),
            }
          : {
              reponsesAcceptees: reponses
                .split('\n')
                .map((r) => r.trim())
                .filter(Boolean),
            }),
        ...(figureSpec === null
          ? {}
          : { figure: figureSpec as unknown as Record<string, unknown> }),
      });
      setSucces(
        `Exercice écrit — ${item.id}. Il est servi dès maintenant, et entre dans la file de relecture.`,
      );
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
    } finally {
      setEnvoi(false);
    }
  }

  /* ── L'aperçu : l'écran entier, à la taille de l'élève ────────────────── */
  if (apercu) {
    return (
      <div className="content__inner">
        <div className="page-head">
          <div>
            <p className="eyebrow">Aperçu élève</p>
            <h1>{titre || 'Sans titre'}</h1>
          </div>
          <button type="button" className="btn btn--ghost" onClick={() => setApercu(false)}>
            Revenir à l’édition
          </button>
        </div>
        <section className="card">
          <p className="lede">{texte || 'L’énoncé apparaîtra ici.'}</p>
          {consigne ? <p className="note-line">{consigne}</p> : null}
          {forme === 'qcm' ? (
            <div className="list" style={{ marginTop: 'var(--espace-4)' }}>
              {options
                .filter((o) => o.texte.trim() !== '')
                .map((o) => (
                  <div className="li" key={o.id}>
                    <span className="li-main">
                      <span className="li-title">{o.texte}</span>
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="note-line">L’élève écrit sa réponse.</p>
          )}
        </section>
        <p className="note-line">
          Rendu avec la mise en page de la console. L’application rend l’énoncé en corps de lecture
          — 17 px, graisse normale — depuis aujourd’hui.
        </p>
      </div>
    );
  }

  return (
    <div className="content__inner">
      <p className="breadcrumb">
        <Link to="/corpus">Banque de savoirs</Link>
      </p>
      <div className="page-head">
        <div>
          <h1>Concevoir un exercice</h1>
          <p className="page-sub">
            Il naîtra en brouillon : entre l’atelier et l’élève, il y a une relecture.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={!pretAEcrire}
          onClick={() => setApercu(true)}
        >
          Aperçu élève
        </button>
      </div>

      {erreur ? (
        <p className="callout callout--rare" role="alert">
          {erreur}
        </p>
      ) : null}
      {succes ? (
        <p className="callout callout--ok" role="status">
          {succes} <Link to="/corpus">Retourner à la banque</Link>
        </p>
      ) : null}

      {/* ── 1 · Le cadrage ───────────────────────────────────────────────── */}
      <section className="card">
        <h3>Pour qui, et sur quoi</h3>
        <p className="card-sub">
          La matière et l’année de l’exercice viennent du savoir : vous ne les saisissez pas.
        </p>
        <div className="form-grid">
          <span className="field">
            <label htmlFor="at-matiere">Matière</label>
            <select
              id="at-matiere"
              className="control"
              value={matiere}
              onChange={(e) => setMatiere(e.target.value)}
            >
              {MATIERES.map((m) => (
                <option key={m} value={m}>
                  {matiereLabel(m)}
                </option>
              ))}
            </select>
          </span>
          <span className="field">
            <label htmlFor="at-niveau">Année</label>
            <select
              id="at-niveau"
              className="control"
              value={niveau}
              onChange={(e) => setNiveau(e.target.value)}
            >
              {NIVEAUX_SCOLAIRES.map((n) => (
                <option key={n} value={n}>
                  {n.replace('sec', '')}
                  <sup></sup> secondaire
                </option>
              ))}
            </select>
          </span>
          {avecSequence ? (
            <span className="field">
              <label htmlFor="at-sequence">Séquence</label>
              <select
                id="at-sequence"
                className="control"
                value={sequence ?? ''}
                onChange={(e) => setSequence((e.target.value || null) as SequenceMaths | null)}
              >
                <option value="">Toutes</option>
                {SEQUENCES_MATHS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </span>
          ) : null}
        </div>

        <div className="form-grid" style={{ marginTop: 'var(--espace-4)' }}>
          <span className="field">
            <label htmlFor="at-savoir">Savoir visé</label>
            <select
              id="at-savoir"
              className="control"
              value={savoirId}
              onChange={(e) => setSavoirId(e.target.value)}
            >
              <option value="">
                {savoirs === null ? 'Chargement…' : `Choisir parmi ${savoirsVisibles.length}…`}
              </option>
              {savoirsVisibles.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.enonce}
                </option>
              ))}
            </select>
          </span>
          <span className="field">
            <label htmlFor="at-notion">Notion travaillée</label>
            <select
              id="at-notion"
              className="control"
              value={notionCode}
              disabled={notions.length === 0}
              onChange={(e) => setNotionCode(e.target.value)}
            >
              <option value="">
                {notions.length === 0 ? 'Choisissez d’abord un savoir' : 'Choisir une notion…'}
              </option>
              {notions.map((n) => (
                <option key={n.id} value={n.code}>
                  {n.ordre}. {n.titre}
                </option>
              ))}
            </select>
          </span>
        </div>
      </section>

      {/* ── 2 · L'écriture, sur toute la largeur ─────────────────────────── */}
      <form className="card" onSubmit={onSubmit} noValidate>
        <h3>L’exercice</h3>
        {!pretAEcrire ? (
          <p className="note-line">
            Choisissez une notion pour commencer : c’est elle qui donne les erreurs types qu’un
            leurre pourra pointer.
          </p>
        ) : null}

        <div className="form-grid">
          <span className="field">
            <label htmlFor="at-forme">Forme</label>
            <select
              id="at-forme"
              className="control"
              value={forme}
              onChange={(e) => setForme(e.target.value)}
            >
              {FORMES.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </span>
          <span className="field">
            <label htmlFor="at-exigence">Exigence</label>
            <select
              id="at-exigence"
              className="control"
              value={exigence}
              onChange={(e) => setExigence(e.target.value)}
            >
              {EXIGENCES.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                </option>
              ))}
            </select>
          </span>
          <span className="field">
            <label htmlFor="at-difficulte">Difficulté (1 à 5)</label>
            <input
              id="at-difficulte"
              className="control"
              type="number"
              min={1}
              max={5}
              value={difficulte}
              onChange={(e) => setDifficulte(Number(e.target.value))}
            />
          </span>
        </div>

        <span className="field field--bloc">
          <label htmlFor="at-titre">Titre</label>
          <input
            id="at-titre"
            className="control"
            required
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
          />
        </span>

        <span className="field field--bloc">
          <label htmlFor="at-texte">Énoncé</label>
          <textarea
            id="at-texte"
            className="control"
            rows={4}
            required
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
          />
        </span>

        <span className="field field--bloc">
          <label htmlFor="at-consigne">Consigne</label>
          <input
            id="at-consigne"
            className="control"
            required
            value={consigne}
            onChange={(e) => setConsigne(e.target.value)}
          />
        </span>

        {forme === 'qcm' ? (
          <fieldset>
            <legend>
              Options — chaque mauvaise réponse doit pointer une erreur type. Un leurre qui ne dit
              rien n’apprend rien, ni à l’élève, ni au professeur, ni au produit.
            </legend>
            {options.map((o, i) => (
              <div className="form-grid" key={o.id}>
                <span className="field">
                  <label htmlFor={`at-opt-${o.id}`}>Option {o.id}</label>
                  <input
                    id={`at-opt-${o.id}`}
                    className="control"
                    value={o.texte}
                    onChange={(e) =>
                      setOptions((l) =>
                        l.map((x, j) => (i === j ? { ...x, texte: e.target.value } : x)),
                      )
                    }
                  />
                </span>
                <span className="field">
                  <label htmlFor={`at-err-${o.id}`}>Erreur visée (vide = bonne réponse)</label>
                  <input
                    id={`at-err-${o.id}`}
                    className="control"
                    placeholder="Code d’erreur type"
                    value={o.erreurType}
                    onChange={(e) =>
                      setOptions((l) =>
                        l.map((x, j) => (i === j ? { ...x, erreurType: e.target.value } : x)),
                      )
                    }
                  />
                </span>
              </div>
            ))}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() =>
                setOptions((l) => [
                  ...l,
                  { id: String.fromCharCode(97 + l.length), texte: '', erreurType: '' },
                ])
              }
            >
              Ajouter une option
            </button>
            {!bonneOption ? (
              <p className="note-line">
                Aucune option n’est marquée comme bonne réponse : laissez le champ « erreur visée »
                vide sur celle qui l’est.
              </p>
            ) : null}
          </fieldset>
        ) : (
          <span className="field field--bloc">
            <label htmlFor="at-reponses">Réponses acceptées — une par ligne</label>
            <textarea
              id="at-reponses"
              className="control"
              rows={3}
              value={reponses}
              onChange={(e) => setReponses(e.target.value)}
            />
          </span>
        )}

        <span className="field field--bloc">
          <label htmlFor="at-explication">Explication rendue après le verdict</label>
          <textarea
            id="at-explication"
            className="control"
            rows={3}
            required
            value={explication}
            onChange={(e) => setExplication(e.target.value)}
          />
        </span>

        <ChampsFigureFonction
          actif={avecFigure}
          onActif={setAvecFigure}
          etat={figure}
          onEtat={setFigure}
          libelleActivation="Joindre la représentation graphique d’une fonction"
        />

        <div className="file-vide-actions">
          <button type="submit" className="btn btn--primary" disabled={!pretAEcrire || envoi}>
            {envoi ? 'Enregistrement…' : 'Enregistrer en brouillon'}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={!pretAEcrire}
            onClick={() => setApercu(true)}
          >
            Aperçu élève
          </button>
          <button type="button" className="btn btn--quiet" onClick={() => navigate('/corpus')}>
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}
